import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Banknote, Activity, CheckCircle, ArrowRight } from 'lucide-react';
import { PLANES } from '@/data/venddi';

export default function VenddiPlans() {
  const [selected, setSelected] = useState('pro');

  return (
    <div id="planes" className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-sky-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-100 border border-sky-200 rounded-full">
            <Banknote className="w-3.5 h-3.5" />ARRIENDO MENSUAL
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight mb-3" style={{ fontFamily: 'Syne,sans-serif' }}>
            Elige el plan según tus <span className="text-[#0EA5E9]">transacciones mensuales</span>
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            Cero fees de instalación. Cero costo por upgrade. Factura mensual en UF. Incluye Kian™ supervisando 24/7.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 max-w-6xl mx-auto" data-testid="venddi-plans">
          {PLANES.map((p) => {
            const isSelected = selected === p.key;
            return (
              <motion.div
                key={p.key}
                whileHover={{ y: -4 }}
                onClick={() => setSelected(p.key)}
                className={`relative cursor-pointer p-6 sm:p-8 rounded-2xl transition-all flex flex-col ${p.featured ? 'bg-[#0A2540] text-white shadow-2xl lg:scale-105' : 'bg-white border-2 border-slate-200 hover:border-[#0EA5E9]/50'} ${isSelected ? 'ring-2 ring-[#38BDF8]' : ''}`}
                data-testid={`venddi-plan-${p.key}`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#38BDF8] text-[#0A2540] text-[10px] font-black uppercase tracking-wider shadow-lg">
                    MÁS ELEGIDO
                  </div>
                )}
                <div className="mb-4">
                  <div className={`text-xs font-bold uppercase tracking-[0.16em] mb-1.5 ${p.featured ? 'text-[#38BDF8]' : 'text-[#0EA5E9]'}`}>{p.name}</div>
                  <h3 className={`text-base font-bold leading-snug ${p.featured ? 'text-white' : 'text-[#0A2540]'}`} style={{ fontFamily: 'Syne,sans-serif' }}>{p.subtitle}</h3>
                </div>

                {/* PRICE — improved alignment */}
                <div className="mb-1 flex items-baseline gap-2 leading-none" style={{ fontFamily: 'Syne,sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  <span className={`text-6xl sm:text-7xl font-black tracking-tight ${p.featured ? 'text-[#38BDF8]' : 'text-[#0A2540]'}`}>{p.price}</span>
                  <span className={`text-xl sm:text-2xl font-bold ${p.featured ? 'text-white' : 'text-[#0A2540]'}`}>{p.unit}</span>
                </div>
                <div className={`text-xs mb-5 ${p.featured ? 'text-slate-300' : 'text-slate-500'}`}>{p.period}</div>

                <div className={`p-2.5 rounded-lg mb-5 text-xs font-semibold flex items-center gap-1.5 ${p.featured ? 'bg-white/10 text-white' : 'bg-sky-50 text-[#0EA5E9]'}`}>
                  <Activity className="w-3.5 h-3.5 shrink-0" />{p.tx}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${p.featured ? 'text-slate-200' : 'text-slate-700'}`}>
                      <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${p.featured ? 'text-[#38BDF8]' : 'text-emerald-500'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-4 border-t border-white/10">
                  <div className={`text-[10px] uppercase tracking-[0.16em] font-bold mb-1 ${p.featured ? 'text-[#38BDF8]' : 'text-[#0EA5E9]'}`}>Ideal para</div>
                  <p className={`text-xs mb-4 ${p.featured ? 'text-slate-300' : 'text-slate-600'}`}>{p.ideal}</p>
                  <Link
                    to={`/contacto?source=venddi-plan-${p.key}`}
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${p.featured ? 'bg-[#38BDF8] text-[#0A2540] hover:bg-sky-300' : 'bg-[#0A2540] text-white hover:bg-[#050E1F]'}`}
                    data-testid={`venddi-plan-cta-${p.key}`}
                  >
                    Arrendar {p.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="max-w-3xl mx-auto mt-10 p-5 rounded-2xl bg-white border border-slate-200 text-center">
          <p className="text-sm text-slate-600">
            <strong className="text-[#0A2540]">¿No sabes qué plan te conviene?</strong> Empieza con el <span className="font-bold text-[#0EA5E9]">Pro</span> (el más elegido). Si te sobra o te falta, migramos sin costo.
          </p>
        </div>
      </div>
    </div>
  );
}
